<template>
  <div class="document-container">
    <es-loader
      v-if="loading"
      style="margin-top: 15rem"
    />
    <es-exception
      v-else-if="loadingError"
      margin="15rem"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <pid-retired-id-panel
        v-if="isRetired"
        :person="person"
      />
      <div class="document">
        <div class="header">
          <es-header>
            <template #header>
              <div class="header-title">
                Matched DirectMatch Records
              </div>
            </template>
          </es-header>
        </div>
        <es-exception
          v-if="!matches.length"
          exceptionMessage="No results found."
          margin="0rem"
          style="padding: 6.5rem"
        />
        <div
          v-else
          class="content table-wrapper"
        >
          <table class="table is-hoverable is-fullwidth">
            <es-sorter
              :apiSorter="apiSorter"
              :columnHeaders="columnHeaders"
              @updateSorter="updateSorter"
            />
            <person-profile-dm-row
              v-for="match in matches"
              :key="match.id"
              :match="match"
              :programs="allPrograms"
            />
          </table>
        </div>
        <div class="pager-container" v-if="totalCount">
          <es-pager
            :totalCount="totalCount"
            :pageSize="pageSize"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import qs from 'qs';
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSorter from '@/components/EsSorter.vue';
import PersonProfileDmRow from '@/components/PersonProfileDmRow.vue';
import PidRetiredIdPanel from '@/components/PidRetiredIdPanel.vue';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  mixins: [
    FieldLabels,
  ],
  components: {
    EsException,
    EsLoader,
    EsHeader,
    EsPager,
    EsSorter,
    PersonProfileDmRow,
    PidRetiredIdPanel,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    sortField: {
      type: String,
    },
    sortOrder: {
      type: String,
    },
    person: {
      type: Object,
      required: true,
    },
    isRetired: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      apiSorter: {},
      columnHeaders: [],
      loading: true,
      loadingError: null,
      matches: [],
      pageSize: 25,
      totalCount: null,
    };
  },
  created() {
    this.getMatches();
    this.loadPersonTypes();
    this.columnHeaders = [
      {
        header: 'Index Date',
        title: 'Index Date',
        field: 'matchTypeFileDate',
        order: 'DESC',
        sortable: true,
      },
      {
        header: 'Date Matched',
        title: 'Date Matched',
        field: 'matchDate',
        order: null,
        sortable: true,
      },
      {
        header: `${this.fieldLabel('SCHOOL_YEAR')}`,
        title: `${this.fieldLabel('SCHOOL_YEAR')}`,
        field: 'schoolYear',
        order: null,
        sortable: true,
      },
      {
        header: 'Match Type',
        title: 'Match Type',
        field: 'matchTypeId',
        order: null,
        sortable: true,
      },
      {
        header: 'Decision',
        title: 'Decision',
        field: null,
        order: null,
        sortable: false,
      },
      {
        header: 'DSS',
        title: 'DSS',
        field: 'programUniqueId',
        order: null,
        sortable: true,
      },
      {
        header: 'Match Option',
        title: 'Match Option',
        field: 'matchOption',
        order: null,
        sortable: true,
      },
      {
        header: 'Location Info',
        title: 'Location Info',
        field: 'districtCode',
        order: null,
        sortable: true,
      },
      {
        header: `${this.fieldLabel('LOCAL_STUDENT_ID')}`,
        title: `${this.fieldLabel('LOCAL_STUDENT_ID')}`,
        field: 'sourceId',
        order: null,
        sortable: true,
      },
      {
        header: `${this.fieldLabel('SOURCE_CODE')}`,
        title: `${this.fieldLabel('SOURCE_CODE')}`,
        field: 'sourceCode',
        order: null,
        sortable: true,
      },
      {
        header: 'Transaction Info',
        title: 'Transaction Info',
        field: 'matchTransId',
        order: null,
        sortable: true,
      },
    ];
  },
  watch: {
    $route() {
      this.getMatches();
    },
  },
  computed: {
    ...mapGetters('lookups', [
      'allPrograms',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
    ]),
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    searchParams() {
      const params = {};
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
    urlParams() {
      const params = {};
      if (this.sortField && this.sortOrder) {
        params.sort = JSON.stringify([{
          field: this.sortField,
          order: this.sortOrder,
        }]);
      }
      params.page = this.$route.query.page ? this.$route.query.page : 1;
      params.pageSize = this.pageSize;
      return params;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.person.personType);
    },
    async getMatches() {
      try {
        this.loading = true;
        this.loadingError = false;
        const response = await axios.get(`${this.isRetired ? 'retired-person' : 'person'}/${this.id}/matches`,
          {
            params: this.urlParams,
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
        this.matches = response.data.data;
        // eslint-disable-next-line prefer-destructuring
        this.apiSorter = response.data.sorters[0];
        this.totalCount = response.data.totalCount;
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    updateSorter() {
      this.$router.replace({ query: this.searchParams });
    },
  },
};
</script>

<style lang="sass" scoped>
  .table-wrapper
    overflow-x: auto
  .pager-container
    border-bottom: 1px solid #dee2e6
    border-width: 1px 0 0
    padding: 5.6px 12px
    background-color: #f9f9f9
</style>
